import React, { useState, useEffect } from 'react';
import StepHeader from './StepHeader';
import { ClientProposalDescriptionInformation, ConsultantBio, WorkflowStep } from './useWorkflow';
import { Dropdown, IDropdownOption, Stack, Text, Label, TextField } from '@fluentui/react';
import { RadioGroup, Radio } from '@fluentui/react-components';
import { useDropzone } from 'react-dropzone';
import { ArrowUploadRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import styles from './Step1.module.scss';
import { getConsultantBioOptions, uploadClientProposal } from '../../../api/api';
import { useAuthErrorContext } from '../../../components/AuthErrorProvider';
import { LoadingOverlay } from '../../../components/LoadingOverlay';

interface Step1Props {
  data: ClientProposalDescriptionInformation | undefined;
  onNext: (data: ClientProposalDescriptionInformation) => void;
  onBack: () => void;
  onStepClick: (step: WorkflowStep) => void;
}

const Step1: React.FC<Step1Props> = ({ data, onNext, onBack, onStepClick }) => {
  const intl = useIntl();
  const { handleAuthError } = useAuthErrorContext();
  const [selectedFile, setSelectedFile] = useState<File | null>(data?.file || null);
  const [profileOptions, setProfileOptions] = useState<IDropdownOption[]>([{ key: '', text: 'Select Profile' }]);
  const [selectedProfiles, setSelectedProfiles] = useState<ConsultantBio[]>(data?.consultingTeam || []);
  const [description, setDescription] = useState<string>(data?.description || '');
  const [uploadType, setUploadType] = useState<string>(data?.type || 'file');
  const [isLoading, setIsLoading] = useState(false);
  const isResuming = Boolean(data?.filename);

  useEffect(() => {
    const fetchConsultantBios = async () => {
      try {
        const options = await getConsultantBioOptions();
        setProfileOptions(options);
      } catch (error) {
        console.error('Error fetching consultant bios:', error);
      }
    };

    fetchConsultantBios();
  }, []);

  const handleNext = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      formData.append('consultingTeam', JSON.stringify(selectedProfiles));
      formData.append('currentStep', WorkflowStep.Step1.toString());

      if (data?.filename) {
        formData.append('filename', data.filename);
      } else if (uploadType === 'file' && selectedFile) {
        formData.append('file', selectedFile);
      } else if (uploadType === 'description') {
        formData.append('description', description);
      }

      const response = await uploadClientProposal({ formData }, handleAuthError);

      if (!response.data) {
        throw new Error('Failed to upload proposal');
      }

      const { filename } = response.data;

      onNext({
        consultingTeam: selectedProfiles,
        type: uploadType as 'file' | 'description',
        file: selectedFile,
        description,
        filename: filename || data?.filename,
      });
    } catch (error) {
      console.error('Error uploading proposal:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
    }
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    noClick: true, // Prevents the default click behavior
  });

  const handleUpload = () => {
    open(); // Manually trigger the file dialog
  };

  const handleProfileSelect = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<ConsultantBio>) => {
    if (option && option.key !== '') {
      setSelectedProfiles(prev => {
        if (prev.length >= 4) {
          return prev;
        }
        if (!prev.some(profile => profile.id === (option.key as string))) {
          return [...prev, { name: option.text, id: option.key as string }];
        }
        return prev;
      });
    }
  };

  const handleProfileRemove = (key: string) => {
    setSelectedProfiles(prev => prev.filter(profile => profile.id !== key));
  };

  const canContinue =
    isResuming ||
    (uploadType === 'file' && selectedFile !== null) ||
    (uploadType === 'description' &&
      description.trim() !== '' &&
      description.length <= 500 &&
      selectedProfiles.length > 0);

  return (
    <Stack className={styles.container}>
      <StepHeader
        onBack={onBack}
        onNext={handleNext}
        currentStep={WorkflowStep.Step1}
        canContinue={canContinue && !isLoading}
        onStepClick={onStepClick}
      />
      {isLoading && <LoadingOverlay message={intl.formatMessage({ id: 'clientProposal.step1.uploading' })} />}
      <Stack className={styles.content}>
        <Stack className={styles.section}>
          <Label required>{intl.formatMessage({ id: 'clientProposal.step1.rolePd.label' })}</Label>
          <Text className={styles.description}>
            {intl.formatMessage({ id: 'clientProposal.step1.rolePd.description' })}
          </Text>

          {isResuming ? (
            <Stack className={styles.resumingFile}>
              <Text variant="medium">{intl.formatMessage({ id: 'clientProposal.step1.resuming.message' })}</Text>
              <Text className={styles.filename}>{data?.filename}</Text>
            </Stack>
          ) : (
            <>
              <RadioGroup
                className={styles.radioGroup}
                value={uploadType}
                onChange={(_, option) => setUploadType(option?.value || 'file')}
              >
                <Radio value="file" label={intl.formatMessage({ id: 'clientProposal.step1.upload.fileUpload' })} />
                <Radio
                  value="description"
                  label={intl.formatMessage({ id: 'clientProposal.step1.upload.addDescription' })}
                />
              </RadioGroup>

              {uploadType === 'file' ? (
                <div {...getRootProps()} className={styles.dropzone}>
                  <input {...getInputProps()} />
                  <Label className={styles.uploadButton} onClick={handleUpload}>
                    <ArrowUploadRegular />
                    {intl.formatMessage({ id: 'clientProposal.step1.upload.button' })}
                  </Label>
                  {selectedFile ? (
                    <ul>
                      <li>{selectedFile.name}</li>
                    </ul>
                  ) : (
                    <Text className={styles.dropzoneText}>
                      {intl.formatMessage({ id: 'clientProposal.step1.upload.fileTypes' })}
                    </Text>
                  )}
                </div>
              ) : (
                <TextField
                  multiline
                  rows={4}
                  value={description}
                  onChange={(_, newValue) => setDescription(newValue || '')}
                  placeholder={intl.formatMessage({ id: 'clientProposal.step1.description.placeholder' })}
                  maxLength={500}
                />
              )}
            </>
          )}
        </Stack>

        <Stack className={styles.section}>
          <Label required>{intl.formatMessage({ id: 'clientProposal.step1.consultingTeam.label' })}</Label>
          <Text className={styles.description}>
            {intl.formatMessage({ id: 'clientProposal.step1.consultingTeam.description' })}
          </Text>
          <Text className={styles.selectedCount}>
            {intl.formatMessage(
              { id: 'clientProposal.step1.consultingTeam.selectedCount' },
              { count: selectedProfiles.length },
            )}
          </Text>

          <Stack className={styles.selectedProfiles}>
            {selectedProfiles.map((profile, index) => (
              <Stack key={profile.id} horizontal className={styles.profileTag}>
                <Text>
                  {profile.name}
                  {index === 0 && (
                    <span className={styles.primaryTag}>
                      {intl.formatMessage({ id: 'clientProposal.step1.consultingTeam.primaryConsultant' })}
                    </span>
                  )}
                </Text>
                <button
                  className={styles.removeButton}
                  title={intl.formatMessage({ id: 'clientProposal.step1.consultingTeam.removeButton' })}
                  aria-label={intl.formatMessage({ id: 'clientProposal.step1.consultingTeam.removeButton' })}
                  onMouseDown={e => {
                    e.preventDefault();
                    handleProfileRemove(profile.id as string);
                  }}
                >
                  <span>✖</span>
                </button>
              </Stack>
            ))}
          </Stack>

          <Dropdown
            placeholder={intl.formatMessage({ id: 'clientProposal.step1.consultingTeam.selectProfile' })}
            options={profileOptions.map(option => ({
              ...option,
              text: selectedProfiles.some(profile => profile.id === option.key) ? `✔️ ${option.text}` : option.text,
            }))}
            onChange={handleProfileSelect}
            selectedKey={''}
            disabled={selectedProfiles.length >= 4}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Step1;
